'use client';

import { useRef, useState, useCallback, useEffect } from 'react';
import {
  SlidersHorizontal, X, Search,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useT } from '@/components/i18n-provider';
import { SearchSuggestions, addRecentSearch } from './search-suggestions';
import type { DiscoverFilters, ContentType, Scope, ViewMode } from './use-discover';

interface DiscoverFilterBarProps {
  filters: DiscoverFilters;
  viewMode: ViewMode;
  activeFilterCount: number;
  onQueryChange: (q: string) => void;
  onToggleType: (t: ContentType) => void;
  onToggleTag: (tag: string) => void;
  onToggleChannel: (ch: string) => void;
  onToggleScope: (s: Scope) => void;
  onClearFilter: (key: keyof DiscoverFilters) => void;
  onClearAll: () => void;
  onViewModeChange: (mode: ViewMode) => void;
  onOpenFilters: () => void;
}

const COUNTRY_NAMES: Record<string, string> = {
  us: 'United States', gb: 'United Kingdom', ca: 'Canada', au: 'Australia',
  de: 'Germany', fr: 'France', jp: 'Japan', cn: 'China', in: 'India',
  br: 'Brazil', mx: 'Mexico', kr: 'South Korea', it: 'Italy', es: 'Spain',
  nl: 'Netherlands', se: 'Sweden', ch: 'Switzerland', sg: 'Singapore',
};

function formatGeoSlug(slug: string): string {
  const parts = slug.split('/');
  if (parts.length === 1) return COUNTRY_NAMES[slug] || slug.toUpperCase();
  const city = parts[parts.length - 1];
  return city.charAt(0).toUpperCase() + city.slice(1) + ', ' + parts[0].toUpperCase();
}

export function DiscoverFilterBar({
  filters, viewMode, activeFilterCount,
  onQueryChange, onToggleType, onToggleTag, onToggleChannel, onToggleScope,
  onClearFilter, onClearAll, onViewModeChange, onOpenFilters,
}: DiscoverFilterBarProps) {
  const t = useT();
  const inputRef = useRef<HTMLInputElement>(null);
  const searchWrapperRef = useRef<HTMLDivElement>(null);
  const [searchFocused, setSearchFocused] = useState(false);
  const [localQuery, setLocalQuery] = useState(filters.query);

  useEffect(() => {
    setLocalQuery(filters.query);
  }, [filters.query]);

  const showSuggestions = searchFocused && !localQuery;

  // Close suggestions on outside click
  useEffect(() => {
    if (!showSuggestions) return;
    const handler = (e: MouseEvent) => {
      if (searchWrapperRef.current && !searchWrapperRef.current.contains(e.target as Node)) {
        setSearchFocused(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showSuggestions]);

  const handleQueryChange = useCallback((value: string) => {
    setLocalQuery(value);
    if (!value) onQueryChange('');
  }, [onQueryChange]);

  const handleSearchSubmit = useCallback(() => {
    const trimmed = localQuery.trim();
    onQueryChange(trimmed);
    if (trimmed) addRecentSearch(trimmed);
  }, [localQuery, onQueryChange]);

  const handleSuggestionSelect = useCallback((q: string) => {
    onQueryChange(q);
    addRecentSearch(q);
    setSearchFocused(false);
    inputRef.current?.blur();
  }, [onQueryChange]);

  return (
    <div className="@container space-y-2">
      {/* Search bar with filter button */}
      <div className="relative flex items-center gap-1.5">
        <div ref={searchWrapperRef} className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            ref={inputRef}
            type="search"
            aria-label={t('nav.search')}
            value={localQuery}
            onChange={(e) => handleQueryChange(e.target.value)}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => handleSearchSubmit()}
            onKeyDown={(e) => { if (e.key === 'Enter') { handleSearchSubmit(); inputRef.current?.blur(); } }}
            placeholder={t('discover.searchPlaceholderShort')}
            className="w-full pl-9 pr-8 py-2 text-sm rounded-xl border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
          />
          {localQuery && (
            <button
              onClick={() => { setLocalQuery(''); onQueryChange(''); inputRef.current?.focus(); }}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 rounded-md hover:bg-accent transition-colors"
            >
              <X className="h-3.5 w-3.5 text-muted-foreground" />
            </button>
          )}
          <SearchSuggestions
            visible={showSuggestions}
            onSelect={handleSuggestionSelect}
          />
        </div>

        <button
          onClick={onOpenFilters}
          className={cn(
            'flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium transition-colors border shrink-0',
            activeFilterCount > 0
              ? 'bg-primary/10 text-primary border-primary/30'
              : 'text-muted-foreground border-border hover:bg-accent hover:text-foreground'
          )}
        >
          <SlidersHorizontal className="h-4 w-4" />
          <span className="hidden @md:inline">{t('discover.filters')}</span>
          {activeFilterCount > 0 && (
            <span className="bg-primary text-primary-foreground rounded-full h-5 min-w-5 flex items-center justify-center text-[11px] font-bold px-1">
              {activeFilterCount}
            </span>
          )}
        </button>

        {/* View mode toggle moved to filter sidebar header */}
      </div>

      {/* Active filter pills */}
      <ActiveFilterPills
        filters={filters}
        onToggleType={onToggleType}
        onToggleTag={onToggleTag}
        onToggleChannel={onToggleChannel}
        onToggleScope={onToggleScope}
        onClearFilter={onClearFilter}
        onClearAll={onClearAll}
      />
    </div>
  );
}

function ActiveFilterPills({
  filters, onToggleType, onToggleTag, onToggleChannel, onToggleScope, onClearFilter, onClearAll,
}: {
  filters: DiscoverFilters;
  onToggleType: (t: ContentType) => void;
  onToggleTag: (tag: string) => void;
  onToggleChannel: (ch: string) => void;
  onToggleScope: (s: Scope) => void;
  onClearFilter: (key: keyof DiscoverFilters) => void;
  onClearAll: () => void;
}) {
  const t = useT();

  const TYPE_LABEL: Record<ContentType, string> = {
    all: t('discover.all'), agents: t('discover.agents'), humans: t('discover.humans'),
    channels: t('discover.channels'), posts: t('discover.posts'), tags: t('discover.tags'), chats: t('discover.chats'),
    widgets: t('discover.widgets'),
  };

  const SCOPE_LABEL: Record<Scope, string> = {
    everyone: t('discover.scopeAll'), mine: t('discover.scopeMine'),
    following: t('discover.scopeFollowing'), saved: t('discover.scopeSaved'),
  };

  const TIME_OPTIONS: { value: string; label: string }[] = [
    { value: 'hour', label: t('discover.timePastHour') },
    { value: 'day', label: t('discover.timeToday') },
    { value: 'week', label: t('discover.timeThisWeek') },
    { value: 'month', label: t('discover.timeThisMonth') },
    { value: 'year', label: t('discover.timeThisYear') },
  ];

  const chips: { key: string; label: string; prefix?: string; onRemove: () => void }[] = [];

  for (const tp of filters.types) {
    chips.push({ key: `type-${tp}`, label: TYPE_LABEL[tp], onRemove: () => onToggleType(tp) });
  }
  for (const s of filters.scopes) {
    chips.push({ key: `scope-${s}`, label: SCOPE_LABEL[s], onRemove: () => onToggleScope(s) });
  }
  for (const tag of filters.tags) {
    chips.push({ key: `tag-${tag}`, label: tag, prefix: '#', onRemove: () => onToggleTag(tag) });
  }
  for (const ch of filters.channels) {
    const isGeo = /^[a-z]{2}(\/|$)/.test(ch);
    chips.push({
      key: `ch-${ch}`,
      label: isGeo ? formatGeoSlug(ch) : ch,
      prefix: isGeo ? undefined : '//',
      onRemove: () => onToggleChannel(ch),
    });
  }
  if (filters.timeRange) {
    chips.push({ key: 'time', label: TIME_OPTIONS.find(o => o.value === filters.timeRange)?.label || filters.timeRange, onRemove: () => onClearFilter('timeRange') });
  }
  if (filters.hasMedia) {
    chips.push({ key: 'media', label: t('discover.hasMedia'), onRemove: () => onClearFilter('hasMedia') });
  }
  if (filters.authorType) {
    chips.push({ key: 'author', label: filters.authorType === 'human' ? t('discover.humansOnly') : t('discover.agentsOnly'), onRemove: () => onClearFilter('authorType') });
  }

  const [expanded, setExpanded] = useState(false);
  const [visibleCount, setVisibleCount] = useState(chips.length);
  const measureRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (expanded) return;
    const el = measureRef.current;
    if (!el) return;
    const chipEls = Array.from(el.querySelectorAll('[data-chip]')) as HTMLElement[];
    if (chipEls.length <= 1) { setVisibleCount(chips.length); return; }
    const firstTop = chipEls[0].offsetTop;
    let count = 0;
    for (const c of chipEls) {
      if (c.offsetTop > firstTop) break;
      count++;
    }
    setVisibleCount(count < chips.length ? count : chips.length);
  }, [chips.length, expanded]);

  if (chips.length === 0) return null;

  const overflows = visibleCount < chips.length;
  const hiddenCount = chips.length - visibleCount;
  const displayedChips = expanded ? chips : chips.slice(0, visibleCount);

  return (
    <div>
      {/* Hidden measurement container — full width, zero height, all chips rendered for measuring */}
      <div
        ref={measureRef}
        className="relative flex items-center gap-1.5 flex-wrap overflow-hidden"
        style={{ height: 0 }}
        aria-hidden="true"
      >
        {chips.map(chip => (
          <span key={chip.key} data-chip
            className="flex items-center gap-1 px-2 py-0.5 text-xs font-medium whitespace-nowrap border border-transparent">
            {chip.prefix}{chip.label}
            <span className="inline-block w-2.5 h-2.5" />
          </span>
        ))}
      </div>

      {/* Visible pills */}
      <div className="flex items-center gap-1.5 flex-wrap">
        {displayedChips.map(chip => (
          <button key={chip.key} onClick={chip.onRemove}
            className="group flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-primary/15 text-primary border border-primary/20 dark:bg-primary/40 dark:text-primary-foreground dark:border-primary/60 whitespace-nowrap hover:bg-primary/20 dark:hover:bg-primary/50 transition-colors">
            {chip.prefix && <span className="text-primary/60">{chip.prefix}</span>}
            {chip.label}
            <X className="h-2.5 w-2.5 opacity-60 group-hover:opacity-100" />
          </button>
        ))}
        {overflows && !expanded && (
          <button onClick={() => setExpanded(true)} className="text-xs text-muted-foreground hover:text-foreground whitespace-nowrap transition-colors">
            {t('discover.showMore', { count: hiddenCount })}
          </button>
        )}
        {expanded && overflows && (
          <button onClick={() => setExpanded(false)} className="text-xs text-muted-foreground hover:text-foreground whitespace-nowrap transition-colors">
            {t('discover.showLess')}
          </button>
        )}
        <button onClick={onClearAll} className="text-xs text-muted-foreground hover:text-foreground whitespace-nowrap transition-colors">
          {t('discover.clearAll')}
        </button>
      </div>
    </div>
  );
}

export { ActiveFilterPills };
